/**
 * MODULE: _shared/frontend-shell/src/components/generative-ui/DataTableCard.tsx
 * PURPOSE: Render structured Field/Value tables from assistant **`data_result`** (CRUD read, tool success, **`record_created`** payloads)
 *
 * DEPENDENCIES:
 *   - ../../hooks/assistant/types: DataResult
 *
 * PUBLIC API:
 *   - DataTableCard: presentational table + footer counts
 *
 * SIDE EFFECTS: None (render only)
 *
 * AGENT SAFETY: 🟢 SAFE
 * FILE SIZE: ~85 / 200 lines
 *
 * Recent: 2026-07-15 [2.2.1583] — Message-only confirm cards (empty rows) hide Field/Value table.
 * Recent: 2026-07-15 [2.2.1570] — Field/Value footer hide locked by Playwright create_account / create_task smokes.
 * RECENT_PRIOR: 2026-07-15 [2.2.1568] — Hide ``Showing N of N`` footer for Field/Value confirm cards (task/meeting/account).
 * Recent_PRIOR: 2026-07-08 [2.2.1553] — Table caption `message` renders via **`AiAssistantMarkdown`** (bold/lists no longer show raw `**`).
 * RECENT_PRIOR: 2026-06-05 [2.2.1288] — CRM **`data_result`** link cells render **`<a target="_blank">`** (assistant **View contact** opens CRM drawer in a new tab); **`dataTableCells`** normalizer.
 * RECENT_PRIOR: 2026-04-03 [2.2.268] — **`data-testid="assistant-data-result"`** for Playwright and stable DOM queries (**`AssistantPage`** + **`AssistantOverlay`**).
 */

import type { ReactNode } from 'react';

import type { DataResult } from '../../hooks/assistant/types';
import { useTenantDefaultCurrency } from '@shared/lib/frontend/hooks/useTenantDefaultCurrency';
import { AiAssistantMarkdown } from '../../design-system/components/AiAssistant/AiAssistantMarkdown';
import { isDataTableLinkCell } from './dataTableCells';

interface DataTableCardProps {
  data: DataResult;
  className?: string;
}

function formatValue(val: unknown): string {
  if (val == null) return '-';
  return String(val);
}

function renderCell(cell: unknown): ReactNode {
  if (isDataTableLinkCell(cell)) {
    const href = cell.href.trim();
    if (!href) return cell.label || '—';
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-primary-600 hover:underline dark:text-primary-400"
      >
        {cell.label || 'View'}
      </a>
    );
  }
  return formatValue(cell);
}

export function DataTableCard({ data, className = '' }: DataTableCardProps) {
  const { entity, columns, rows, total_count, showing, message, summary } = data;
  const { formatCurrency } = useTenantDefaultCurrency();

  const totalValue = summary?.total_value;
  const totalValueFormatted = totalValue != null ? formatCurrency(totalValue) : null;
  // Field/Value confirm cards (create task/meeting/account) — hide list-style "Showing N of N entity".
  const isFieldValueConfirm =
    columns.length === 2 &&
    columns[0]?.toLowerCase() === 'field' &&
    columns[1]?.toLowerCase() === 'value';
  const hasRows = Array.isArray(rows) && rows.length > 0;

  return (
    <div
      data-testid="assistant-data-result"
      className={`mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden ${className}`}
    >
      {(totalValueFormatted || message) && (
        <div
          className={`px-3 py-2 bg-gray-50 dark:bg-gray-700/50 text-xs text-gray-600 dark:text-gray-300 ${
            hasRows ? 'border-b border-gray-200 dark:border-gray-600' : ''
          }`}
        >
          {totalValueFormatted && (
            <span className="font-medium text-gray-900 dark:text-white">{totalValueFormatted} total</span>
          )}
          {totalValueFormatted && message && (
            <div className="mt-1 border-t border-gray-200/80 pt-1 dark:border-gray-600/80" />
          )}
          {message ? <AiAssistantMarkdown content={message} compact /> : null}
        </div>
      )}
      {hasRows ? (
        <div className="overflow-x-auto max-h-48 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-600">
                {columns.map((col) => (
                  <th
                    key={col}
                    className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={i}
                  className="border-b border-gray-100 dark:border-gray-700/50 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                >
                  {row.map((cell, j) => (
                    <td key={j} className="px-3 py-2 text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {renderCell(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {!isFieldValueConfirm && hasRows && (
        <div
          data-testid="assistant-data-result-footer"
          className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700"
        >
          Showing {showing} of {total_count} {entity}
        </div>
      )}
    </div>
  );
}
